import { Injectable, signal, computed } from '@angular/core';
import { TreeItem } from './tree.model';

@Injectable({
  providedIn: 'root'
})
export class UndoRedoService {

  private undoStack: TreeItem[][] = [];
  private redoStack: TreeItem[][] = [];
  private maxHistorySize = 50;

  private _undoCount = signal(0);
  private _redoCount = signal(0);

  readonly canUndo = computed(() => this._undoCount() > 0);
  readonly canRedo = computed(() => this._redoCount() > 0);

  /**
   * Callback registered by ContentEditorComponent to capture a snapshot
   * before any tree mutation (from any TreeComponent instance).
   */
  private _beforeMutationCallback: (() => void) | null = null;

  /** Register a callback to be invoked before tree mutations. */
  onBeforeMutation(callback: () => void): void {
    this._beforeMutationCallback = callback;
  }

  /** Called by TreeComponent before any mutation. */
  notifyBeforeMutation(): void {
    if (this._beforeMutationCallback) {
      this._beforeMutationCallback();
    }
  }

  /**
   * Deep-clone and push current state onto the undo stack.
   * Clears the redo stack (new mutation branch).
   */
  pushState(items: TreeItem[]): void {
    const snapshot = this.deepClone(items);
    this.undoStack.push(snapshot);

    // Trim oldest entries beyond max history
    if (this.undoStack.length > this.maxHistorySize) {
      this.undoStack.splice(0, this.undoStack.length - this.maxHistorySize);
    }

    // Any new mutation invalidates the redo branch
    this.redoStack = [];

    this._undoCount.set(this.undoStack.length);
    this._redoCount.set(0);
  }

  /**
   * Undo: push current state to redo, pop and return the previous state.
   * Returns null if nothing to undo.
   */
  undo(currentItems: TreeItem[]): TreeItem[] | null {
    if (this.undoStack.length === 0) return null;

    // Save current state so we can redo later
    this.redoStack.push(this.deepClone(currentItems));

    const restored = this.undoStack.pop()!;

    this._undoCount.set(this.undoStack.length);
    this._redoCount.set(this.redoStack.length);

    return restored;
  }

  /**
   * Redo: push current state to undo, pop and return the next state.
   * Returns null if nothing to redo.
   */
  redo(currentItems: TreeItem[]): TreeItem[] | null {
    if (this.redoStack.length === 0) return null;

    // Save current state so we can undo back
    this.undoStack.push(this.deepClone(currentItems));

    const restored = this.redoStack.pop()!;

    this._undoCount.set(this.undoStack.length);
    this._redoCount.set(this.redoStack.length);

    return restored;
  }

  /**
   * Clear all history (e.g. when loading new content).
   */
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this._undoCount.set(0);
    this._redoCount.set(0);
  }

  private deepClone(items: TreeItem[]): TreeItem[] {
    return JSON.parse(JSON.stringify(items));
  }
}
